import { useEffect, useState } from 'react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Combobox } from '@/components/Combobox'
import { Switch } from '@/components/ui/switch'
import { useToast } from '@/hooks/useToast'
import { useCreateRule, useUpdateRule, useInboxCategories } from '@/features/inbox/queries'
import { useWorkflows } from '@/features/workflows/queries'
import type { InboxWatchRule } from '@/features/inbox/types'

export function WatchRuleFormModal({
  open,
  onOpenChange,
  rule,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  rule: InboxWatchRule | null
}) {
  const { toast } = useToast()
  const createRule = useCreateRule()
  const updateRule = useUpdateRule()
  const categoriesQuery = useInboxCategories()
  const workflowsQuery = useWorkflows()

  const [name, setName] = useState('')
  const [matchFrom, setMatchFrom] = useState('')
  const [matchSubject, setMatchSubject] = useState('')
  const [matchKeywords, setMatchKeywords] = useState('')
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [workflowId, setWorkflowId] = useState<string | null>(null)
  const [isActive, setIsActive] = useState(true)
  const [autoConfirm, setAutoConfirm] = useState(false)

  useEffect(() => {
    if (open) {
      setName(rule?.name ?? '')
      setMatchFrom(rule?.match_from ?? '')
      setMatchSubject(rule?.match_subject ?? '')
      setMatchKeywords(rule?.match_keywords?.join(', ') ?? '')
      setCategoryId(rule?.assign_category_id ?? null)
      setWorkflowId(rule?.workflow_id ?? null)
      setIsActive(rule?.is_active ?? true)
      setAutoConfirm(rule?.auto_confirm ?? false)
    }
  }, [open, rule])

  const keywordList = matchKeywords
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean)

  const categories = categoriesQuery.data?.categories ?? []
  const workflows = workflowsQuery.data?.workflows ?? []
  const busy = createRule.isPending || updateRule.isPending

  const onSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Rule name is required', variant: 'destructive' })
      return
    }
    if (!matchFrom.trim() && !matchSubject.trim() && keywordList.length === 0) {
      toast({ title: 'Add a sender, subject or keyword match', variant: 'destructive' })
      return
    }
    const body = {
      name,
      match_from: matchFrom.trim() || null,
      match_subject: matchSubject.trim() || null,
      match_keywords: keywordList.length ? keywordList : null,
      assign_category_id: categoryId,
      workflow_id: workflowId,
      is_active: isActive,
      auto_confirm: autoConfirm,
    }
    try {
      if (rule) {
        await updateRule.mutateAsync({ id: rule.id, body })
        toast({ title: 'Rule updated' })
      } else {
        await createRule.mutateAsync(body)
        toast({ title: 'Rule created' })
      }
      onOpenChange(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed'
      toast({ title: 'Could not save rule', description: msg, variant: 'destructive' })
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={rule ? 'Edit watch rule' : 'New watch rule'}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <FormField
          label="Rule name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Consultant invoices"
        />
        <FormField
          label="Sender contains"
          value={matchFrom}
          onChange={(e) => setMatchFrom(e.target.value)}
          placeholder="e.g. @acme.com"
        />
        <FormField
          label="Subject contains"
          value={matchSubject}
          onChange={(e) => setMatchSubject(e.target.value)}
          placeholder="e.g. invoice"
        />
        <FormField
          label="Keywords (comma separated, any match)"
          value={matchKeywords}
          onChange={(e) => setMatchKeywords(e.target.value)}
          placeholder="e.g. invoice, receipt, statement"
          hint="Matches the subject, sender or preview text. Used to sort mail without AI."
        />
        <FormField
          label="Categorize as"
          control={
            <Combobox
              value={categoryId}
              onChange={setCategoryId}
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
              placeholder="Keep AI classification"
            />
          }
        />
        <FormField
          label="Also trigger workflow (optional)"
          control={
            <Combobox
              value={workflowId}
              onChange={setWorkflowId}
              options={workflows.map((w) => ({ value: w.id, label: w.name }))}
              placeholder="No workflow"
              emptyMessage="No workflows yet"
            />
          }
        />
        <FormField
          label="Active"
          control={<Switch checked={isActive} onCheckedChange={setIsActive} />}
        />
        <FormField
          label="Auto-confirm matched financial items (still a draft, never auto-posts)"
          control={<Switch checked={autoConfirm} onCheckedChange={setAutoConfirm} />}
        />
      </div>
    </Modal>
  )
}
